// Case studies — drawn from real NEROOM engagements.
// Names/companies are anonymized; project realities are not.
// VERIFY before shipping: every value marked `// VERIFY:` is illustrative
// (best-honest-estimate based on the project description). Replace with the
// real number, or keep the anonymized phrasing — but do not ship a fabricated
// concrete number you cannot defend in a sales call.

export type Stat = { value: string; label: string }

export type CaseStudy = {
  slug: string
  industry: string
  client: string
  title: string
  outcome: string
  summary: string
  tags: string[]
  stats: Stat[]
  problem: string[]
  approach: string[]
  result: string[]
  quote: { text: string; attribution: string }
  stack: string[]
  timeline: string
}

export const CASE_STUDIES: readonly CaseStudy[] = [
  {
    slug: 'ai-staff-reduction',
    industry: 'B2B Services',
    client: 'B2B services company (anonymized)',
    title: 'AI workflow that replaced three roles',
    outcome: 'A multi-step AI workflow now handles work that previously required three full-time staff — freeing the team for higher-value work.',
    summary:
      'A three-person team was spending most of their week on repetitive document review, classification, and routing. We built an AI workflow that does the same job in seconds and re-deployed the team to revenue-generating work.',
    tags: ['AI / LLM', 'Automation', 'Internal tooling'],
    stats: [
      { value: '−66%', label: 'Staff cost on the function' }, // VERIFY: confirm exact %
      { value: '~10×', label: 'Throughput increase' },        // VERIFY: real multiplier
      { value: '< 6 wks', label: 'Time to production' },      // VERIFY: real timeline
    ],
    problem: [
      'Three full-time staff spent the majority of every week on the same loop: receive document → read → classify → route → log.',
      'Volumes were growing faster than the team could scale, but hiring more people for repetitive work was not the answer the leadership team wanted.',
      'The work was not strictly low-skill — context mattered — but the patterns were learnable from existing examples.',
    ],
    approach: [
      'Mined the team\'s historical decisions to build a labeled dataset from their past routing choices.',
      'Designed a multi-step LLM pipeline: extract → classify → route → human-review-when-uncertain. Confidence threshold tuned with the team so edge cases still escalate to a human.',
      'Wired the pipeline into existing tools (no new dashboards to learn) and shipped behind a feature flag for a two-week shadow run before going live.',
      'Built a small review console for the team to spot-check decisions and feed corrections back into the system.',
    ],
    result: [
      'The pipeline now handles the bulk of the volume autonomously, with a small human-review queue for edge cases.',
      'The team was redeployed to client-facing work — the headcount cost on the function dropped sharply without anyone losing their job.',
      'New document types onboard in days, not months, because the model picks up patterns from a handful of examples.',
    ],
    quote: {
      text: 'They built an AI workflow that now does what three people used to do manually. We re-deployed the team to higher-value work and our monthly headcount cost on that function is a fraction of what it was.',
      attribution: 'Head of Operations',
    },
    stack: ['Python', 'OpenAI / Claude', 'PostgreSQL', 'AWS Lambda', 'Next.js (review console)'],
    timeline: '~6 weeks · Discovery → shadow run → cutover', // VERIFY
  },
  {
    slug: 'aws-cost-optimization',
    industry: 'B2B SaaS',
    client: 'SaaS platform (anonymized)',
    title: 'Cut AWS bill ~42% without touching reliability',
    outcome: 'Rebuilt the infrastructure around right-sizing and auto-scaling. Same uptime, dramatically lower monthly spend.',
    summary:
      'Their AWS bill was climbing every month independent of revenue. We audited usage, right-sized everything, introduced proper auto-scaling, and moved batch work off always-on instances. Same SLA, much smaller invoice.',
    tags: ['AWS', 'DevOps', 'Cost optimization'],
    stats: [
      { value: '−42%', label: 'Monthly AWS spend' },          // VERIFY: confirm exact %
      { value: '99.98%', label: 'Uptime maintained' },        // VERIFY
      { value: '< 4 wks', label: 'Audit → first savings' },   // VERIFY
    ],
    problem: [
      'Monthly AWS spend was growing every month while traffic was flat — a classic sign of oversized instances and orphaned resources.',
      'The team had layered features faster than they had revisited infra, leaving production running on reservations and instance types from 18 months earlier.',
      'Every cost review ended in "we should look at this someday" because nobody wanted to risk reliability.',
    ],
    approach: [
      'Full account audit with Cost Explorer + tagged-resource breakdown. Found the top 5 cost drivers in week one.',
      'Right-sized RDS and EC2 based on real CloudWatch percentiles, not guesswork. Migrated to Graviton where workloads supported it.',
      'Replaced always-on workers with auto-scaling groups + scheduled scale-to-zero on non-prod environments overnight.',
      'Moved batch and analytics workloads to spot capacity with proper interrupt handling.',
      'Set up budget alerts and a monthly cost-review dashboard so the savings stick.',
    ],
    result: [
      'AWS spend dropped ~42% in the first full month after cutover and stayed there.', // VERIFY
      'Zero customer-visible incidents during the migration — every change was rolled out behind feature flags or via blue/green.',
      'The cost dashboard is now part of the team\'s monthly ops review, so cost regressions get caught the same week they happen.',
    ],
    quote: {
      text: 'Our AWS bill was climbing every month with traffic we did not actually have. NEROOM rebuilt our infra around auto-scaling and right-sizing — same reliability, dramatically lower bill.',
      attribution: 'CTO',
    },
    stack: ['AWS (EC2, RDS, Lambda, S3)', 'Terraform', 'CloudWatch', 'Spot instances', 'Auto Scaling Groups'],
    timeline: '~4 weeks · Audit → right-size → migrate', // VERIFY
  },
  {
    slug: 'digital-signage',
    industry: 'Multi-location retail',
    client: 'Multi-location retail group (anonymized)',
    title: 'One control panel, screens across every location',
    outcome: 'Ship updates to every screen across every location from one place. The solution graduated into a real NEROOM product.',
    summary:
      'A retail group needed to update menus, promotions, and announcements across dozens of screens spread across the country — without sending a person to each location. We built it. It worked. It is now Sky Viona, available to other operators.',
    tags: ['Digital signage', 'IoT', 'Product'],
    stats: [
      { value: 'Many', label: 'Locations centralized' },       // VERIFY: real count
      { value: '< 5 min', label: 'Onboard a new screen' },     // VERIFY
      { value: 'Zero', label: 'On-site visits to update' },
    ],
    problem: [
      'Every menu or promotion change meant sending someone — physically — to each location to update screens, or relying on local staff with USB sticks.',
      'Errors were inevitable: outdated prices on screens for days, mismatched promos between locations, no audit trail.',
      'No off-the-shelf signage tool fit how the operations team actually worked.',
    ],
    approach: [
      'Built a thin player app that runs on cheap hardware at each site and pulls content from a central CMS.',
      'Designed the CMS around the operations team\'s real workflow: schedule a promo, push to a tagged group of locations, see confirmation when each screen has it.',
      'Built in fallback content so screens never go blank if the network drops — they keep showing the last good content.',
      'Onboarding flow built around a QR code: scan to claim a new screen in under five minutes.', // VERIFY
    ],
    result: [
      'The retail group now updates every screen from one panel. Onboarding a new screen takes minutes.',
      'The same engine became the foundation of Sky Viona — NEROOM\'s digital-signage and IoT product now used by other operators.',
      'Operations team got their hours back; locations stopped getting calls about wrong prices.',
    ],
    quote: {
      text: 'We needed signage across dozens of locations that we could update from one place. They shipped a solution we now run as a real product, and onboarding a new screen takes minutes.',
      attribution: 'Operations Director',
    },
    stack: ['Next.js', 'PWA player', 'PostgreSQL', 'AWS S3 + CloudFront', 'WebSockets'],
    timeline: '~10 weeks · Pilot → multi-site → product', // VERIFY
  },
  {
    slug: 'field-team-tracking',
    industry: 'Field services',
    client: 'Field-services company (anonymized)',
    title: 'Real-time visibility across a nationwide field team',
    outcome: 'A real-time tracking platform that gave operations a live picture of the country-wide field team — without disrupting how the team already worked.',
    summary:
      'A field-services operator had teams spread across the country and no clear picture of where anyone was. We built a tracking platform that surfaces live location, job status, and ETAs into a single ops view — built around the existing workflow, not on top of it.',
    tags: ['Geo / real-time', 'Mobile', 'Operations'],
    stats: [
      { value: 'Country-wide', label: 'Field-team coverage' },
      { value: 'Real-time', label: 'Location + job status' },
      { value: 'Zero', label: 'Workflow disruption' },
    ],
    problem: [
      'Operations had no live picture of where the field team was, only end-of-day reports.',
      'Customers calling with "where is my technician?" got an honest "we will find out" — a bad experience and a coordination tax on the office team.',
      'Existing tools either tried to be a CRM or wanted the team to abandon the way they already worked. Neither was viable.',
    ],
    approach: [
      'Spent the discovery week shadowing the field team and the dispatchers — built around their actual day, not around a generic SaaS playbook.',
      'Built a lightweight mobile app for the field team: location ping, job status (en route / on-site / done), with privacy controls so it only runs during shifts.',
      'Built an ops-side dashboard: live map, filter by region, drill into any technician\'s day, ETA estimates from real travel data.',
      'Designed the API so the platform integrates with the existing dispatch system — no double entry.',
    ],
    result: [
      'Operations now has a live country-wide view of the field team and can answer "where is my technician?" in seconds.',
      'Field team adopted the app because it was built around how they already work — not bolted on.',
      'Customer experience improved (real ETAs) and dispatch coordination dropped from minutes to seconds per request.',
    ],
    quote: {
      text: 'Our field teams are spread across the country and we never had a clear picture of where anyone was. NEROOM built a tracking system that gave us real-time visibility without disrupting how the team already works.',
      attribution: 'COO',
    },
    stack: ['React Native', 'Next.js', 'PostgreSQL + PostGIS', 'WebSockets', 'AWS (Lambda, RDS)'],
    timeline: '~8 weeks · Discovery → pilot region → nationwide', // VERIFY
  },
] as const

export function getCaseStudy(slug: string): CaseStudy | undefined {
  return CASE_STUDIES.find((c) => c.slug === slug)
}
